import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

// project imports
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';

// assets
import { IconBell } from '@tabler/icons-react';

const categories = [
  { value: 'all', label: 'All Notifications' },
  { value: 'hardware', label: 'Lockers & Hardware' },
  { value: 'order', label: 'Deliveries & Orders' },
  { value: 'security', label: 'Security & Access' },
  { value: 'unread', label: 'Unread Only' }
];

const STORAGE_KEY = 'mybox_read_notification_ids';

function getStoredReadIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch (e) {
    return new Set();
  }
}

function saveStoredReadIds(idsSet) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(idsSet)));
  } catch (e) {}
}

// ==============================|| NOTIFICATION SECTION ||============================== //

export default function NotificationSection() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [readIds, setReadIds] = useState(() => getStoredReadIds());

  const anchorRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api2fa/api/notifications/feed?limit=60');
      if (res.data?.code === 0 && res.data?.value?.list) {
        setNotifications(res.data.value.list);
      }
    } catch (e) {
      console.error('Error fetching notifications feed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleToggle = () => {
    setOpen((prevOpen) => {
      if (!prevOpen) {
        fetchNotifications();
      }
      return !prevOpen;
    });
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMarkRead = (id) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveStoredReadIds(next);
      return next;
    });
  };

  const handleMarkAllAsRead = () => {
    setReadIds((prev) => {
      const next = new Set(prev);
      notifications.forEach((n) => next.add(n.id));
      saveStoredReadIds(next);
      return next;
    });
  };

  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !readIds.has(n.id)).length;
  }, [notifications, readIds]);

  const filteredNotifications = useMemo(() => {
    if (selectedCategory === 'unread') {
      return notifications.filter((n) => !readIds.has(n.id));
    }
    if (selectedCategory !== 'all') {
      return notifications.filter((n) => n.category === selectedCategory);
    }
    return notifications;
  }, [notifications, selectedCategory, readIds]);

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <ButtonBase
          ref={anchorRef}
          aria-controls={open ? 'notification-menu-list' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          sx={{
            width: 38,
            height: 38,
            minWidth: 38,
            borderRadius: '8px',
            bgcolor: open ? '#F0FDFA' : 'transparent',
            color: open ? '#0D9488' : '#475569',
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s ease-in-out',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(20, 184, 166, 0.15)' : '#F1F5F9',
              color: '#0D9488'
            }
          }}
        >
          <IconBell stroke={1.5} size={22} />

          {/* Subdued unread indicator */}
          {unreadCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: '#0D9488'
              }}
            />
          )}
        </ButtonBase>
      </Tooltip>

      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[{ name: 'offset', options: { offset: [downMD ? 5 : 0, 10] } }]}
        sx={{ zIndex: 1200 }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
              <Paper
                sx={{
                  width: 380,
                  maxWidth: 'calc(100vw - 24px)',
                  borderRadius: '12px',
                  border: theme.palette.mode === 'dark' ? (theme.palette.border?.default ? `1px solid ${theme.palette.border.default}` : '1px solid rgba(255, 255, 255, 0.08)') : '1px solid #E2E8F0',
                  boxShadow: theme.palette.mode === 'dark' ? '0 12px 32px rgba(0, 0, 0, 0.45)' : '0 4px 20px rgba(15, 23, 42, 0.08)',
                  bgcolor: theme.palette.mode === 'dark' ? (theme.palette.background?.raised || '#272B33') : '#FFFFFF',
                  overflow: 'hidden'
                }}
              >
                {/* Header */}
                <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                  <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: 600,
                          color: theme.palette.mode === 'dark' ? (theme.palette.text?.primary || '#ECEEF1') : '#0F172A',
                          fontSize: '15px',
                          letterSpacing: '-0.01em'
                        }}
                      >
                        Notifications
                      </Typography>
                      {unreadCount > 0 && (
                        <Box
                          sx={{
                            fontSize: '11px',
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 500,
                            color: theme.palette.mode === 'dark' ? (theme.palette.text?.secondary || '#A4ABB8') : '#64748B',
                            bgcolor: theme.palette.mode === 'dark' ? (theme.palette.background?.hover || '#2A2E36') : '#F1F5F9',
                            px: '7px',
                            py: '1.5px',
                            borderRadius: '10px'
                          }}
                        >
                          {unreadCount}
                        </Box>
                      )}
                    </Stack>

                    {unreadCount > 0 && (
                      <Typography
                        component="button"
                        onClick={handleMarkAllAsRead}
                        sx={{
                          border: 'none',
                          background: 'none',
                          padding: 0,
                          fontFamily: 'Poppins, sans-serif',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: theme.palette.mode === 'dark' ? '#2DD4BF' : '#0D9488',
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#0F766E',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        Mark all as read
                      </Typography>
                    )}
                  </Stack>

                  <Autocomplete
                    size="small"
                    disableClearable
                    options={categories}
                    getOptionLabel={(option) => option.label || ''}
                    value={categories.find(c => c.value === selectedCategory) || categories[0]}
                    onChange={(event, newValue) => setSelectedCategory(newValue ? newValue.value : 'all')}
                    sx={{ width: '100%' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '32px',
                            minHeight: '32px',
                            padding: '0 10px',
                            fontFamily: 'Poppins, sans-serif',
                            fontSize: '12.5px',
                            color: theme.palette.mode === 'dark' ? (theme.palette.text?.primary || '#ECEEF1') : '#334155',
                            backgroundColor: theme.palette.mode === 'dark' ? (theme.palette.background?.input || '#1A1D23') : '#FFFFFF',
                            borderRadius: '6px',
                            '& fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? (theme.palette.border?.default || 'rgba(255, 255, 255, 0.08)') : '#E2E8F0',
                            }
                          }
                        }}
                      />
                    )}
                  />
                </Box>

                <Divider sx={{ borderColor: theme.palette.mode === 'dark' ? (theme.palette.border?.subtle || 'rgba(255, 255, 255, 0.05)') : '#F1F5F9' }} />

                {/* Notifications List */}
                <Box
                  sx={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-thumb': {
                      bgcolor: theme.palette.mode === 'dark' ? (theme.palette.background?.hover || '#2A2E36') : '#CBD5E1',
                      borderRadius: '4px'
                    }
                  }}
                >
                  <NotificationList
                    notifications={filteredNotifications}
                    readIds={readIds}
                    loading={loading}
                    onMarkRead={handleMarkRead}
                  />
                </Box>

                {/* Footer Action */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderTop: `1px solid ${theme.palette.mode === 'dark' ? (theme.palette.border?.subtle || 'rgba(255, 255, 255, 0.05)') : '#F1F5F9'}`,
                    bgcolor: theme.palette.mode === 'dark' ? (theme.palette.background?.input || '#1A1D23') : '#FAFAFA',
                    textAlign: 'center'
                  }}
                >
                  <Typography
                    component="button"
                    onClick={() => {
                      setOpen(false);
                      navigate('/system/audit-logs');
                    }}
                    sx={{
                      border: 'none',
                      background: 'none',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: theme.palette.mode === 'dark' ? '#2DD4BF' : '#0D9488',
                      cursor: 'pointer',
                      '&:hover': {
                        color: '#0F766E',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    View All System & Locker Logs
                  </Typography>
                </Box>
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
